
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { motion, cubicBezier } from 'framer-motion';
import { FileTextIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage } from '../types';
import { useSEO } from '../hooks/useSEO';
import Footer from '../components/Footer';
import { CONTACT_INFO, APP_CONFIG } from '../config';

interface LegalPageProps {
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const LegalPage: React.FC<LegalPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();

  useSEO(
    t('seo_legal_title'),
    t('seo_legal_description'),
    [t('seo_legal_kw1'), t('seo_legal_kw2'), t('seo_legal_kw3'), t('seo_legal_kw4')]
  );

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const currentDate = new Date().toLocaleDateString('en-GB');

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-4xl w-full bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 md:p-10 mb-8 border border-gray-200 dark:border-gray-700">
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-10">
          <FileTextIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-4" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white text-center leading-tight">
            {t('legalPage_title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mt-3 text-center max-w-2xl">
            {t('legalPage_tagline')}
          </p>
        </motion.div>

        <div className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
          {/* Impressum Section */}
          <motion.section id="impressum" variants={itemVariants} className="mb-12 scroll-mt-24">
            <h2 className="font-serif text-2xl font-bold mb-4">{t('legalPage_impressum_title')}</h2>
            <div className="space-y-4">
              <p><strong>Company Name:</strong> {CONTACT_INFO.companyName}</p>
              <p><strong>Address:</strong><br />
                {CONTACT_INFO.address.street}<br />
                {CONTACT_INFO.address.zip} {CONTACT_INFO.address.city}<br />
                {CONTACT_INFO.address.country}
              </p>
              <p><strong>Email:</strong> <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">{CONTACT_INFO.email.general}</a></p>
              {/* <p><strong>Phone:</strong> {CONTACT_INFO.phone.main}</p> */}
              <p><strong>Website:</strong> <a href={APP_CONFIG.DOMAIN} className="text-indigo-600 dark:text-indigo-400 hover:underline">{APP_CONFIG.DOMAIN}</a></p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Representative: Konstantina, Founder & Styling Advisor
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {/* VAT ID: CHE-XXX.XXX.XXX (Swiss VAT Number - to be inserted) */}
              </p>
            </div>
          </motion.section>

          {/* Terms and Conditions */}
          <motion.section id="terms" variants={itemVariants} className="mb-12 scroll-mt-24">
            <h2 className="font-serif text-2xl font-bold mb-4">{t('legalPage_terms_title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Last updated: {currentDate}</p>

            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-xl mb-2">1. Scope and Acceptance</h3>
                <p>These Terms and Conditions ("Terms") govern your access to and use of the Constantina Lifestyle styling services and web application ("Service") provided by {CONTACT_INFO.companyName}. By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, please discontinue use immediately.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">2. Services Offered</h3>
                <p>Constantina Lifestyle provides AI-powered virtual styling services, including but not limited to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Virtual try-on and outfit visualization</li>
                  <li>Personal color analysis</li>
                  <li>Style recommendations and fashion advice</li>
                  <li>Wardrobe management tools</li>
                  <li>Shopping recommendations and affiliate links</li>
                  <li>Consultation booking services</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">3. User Accounts and Membership</h3>
                <p>3.1. <strong>Registration:</strong> You may use certain features as a guest or create an account for full access.</p>
                <p>3.2. <strong>Membership Tiers:</strong> We offer Basic, Premium, and Advanced membership levels with varying features and monthly generation limits.</p>
                <p>3.3. <strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
                <p>3.4. <strong>Truthful Information:</strong> You agree to provide accurate and complete information during registration.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">4. Payment Terms (Swiss Law Compliance)</h3>
                <p>4.1. <strong>Pricing:</strong> All prices are displayed in EUR and converted to your local currency. Swiss customers will be charged in CHF at the current exchange rate.</p>
                <p>4.2. <strong>Payment Methods:</strong> We accept credit cards, debit cards, and other payment methods as displayed.</p>
                <p>4.3. <strong>Subscriptions:</strong> Membership subscriptions renew automatically unless canceled before the renewal date.</p>
                <p>4.4. <strong>Cancellation:</strong> You may cancel your subscription at any time through your profile settings. Cancellation takes effect at the end of the current billing period.</p>
                <p>4.5. <strong>No Refunds:</strong> Payments are non-refundable except as required by Swiss consumer protection law.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">5. Intellectual Property Rights</h3>
                <p>5.1. <strong>Service Content:</strong> All text, graphics, logos, images, and software are the property of {CONTACT_INFO.companyName} or its licensors.</p>
                <p>5.2. <strong>User Content:</strong> You retain ownership of photos and images you upload. By uploading, you grant us a limited license to process and display your content for providing the Service.</p>
                <p>5.3. <strong>AI-Generated Content:</strong> Outfit images and styling recommendations generated through our AI tools are provided for your personal use only.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">6. User Conduct and Prohibited Uses</h3>
                <p>You agree NOT to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Use the Service for any illegal or unauthorized purpose</li>
                  <li>Upload offensive, inappropriate, or copyrighted content without permission</li>
                  <li>Attempt to reverse-engineer, hack, or compromise the Service</li>
                  <li>Resell or redistribute AI-generated content commercially</li>
                  <li>Exceed usage limits or abuse API access</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">7. Right of Withdrawal (Swiss Consumer Protection)</h3>
                <p>Under Swiss law (Swiss Code of Obligations, Art. 40a-40l), consumers have a 14-day right of withdrawal for distance contracts. However, this right does not apply to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Digital content that has been fully provided with your express consent</li>
                  <li>Personalized services that have been initiated</li>
                  <li>Subscription services after the first billing period has begun</li>
                </ul>
                <p className="mt-2">To exercise your right of withdrawal, contact us at <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400">{CONTACT_INFO.email.general}</a>.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">8. Limitation of Liability</h3>
                <p>8.1. The Service is provided "as is" without warranties of any kind.</p>
                <p>8.2. We are not liable for indirect, incidental, or consequential damages arising from use of the Service.</p>
                <p>8.3. Our total liability shall not exceed the amount you paid for the Service in the past 12 months.</p>
                <p>8.4. Nothing in these Terms limits liability for death, personal injury, or fraud.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">9. Termination</h3>
                <p>We reserve the right to suspend or terminate your account for violation of these Terms, at our sole discretion.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">10. Governing Law and Jurisdiction</h3>
                <p>These Terms are governed by Swiss law. Any disputes shall be resolved in the courts of Bern, Switzerland, subject to mandatory consumer protection laws in your jurisdiction.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">11. Changes to Terms</h3>
                <p>We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the new Terms.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">12. Contact</h3>
                <p>For questions about these Terms, contact us at:</p>
                <p className="mt-2">
                  Email: <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400">{CONTACT_INFO.email.general}</a><br />
                  Address: {CONTACT_INFO.address.street}, {CONTACT_INFO.address.zip} {CONTACT_INFO.address.city}, {CONTACT_INFO.address.country}
                </p>
              </div>
            </div>
          </motion.section>

          {/* Privacy Policy */}
          <motion.section id="privacy" variants={itemVariants} className="mb-12 scroll-mt-24">
            <h2 className="font-serif text-2xl font-bold mb-4">{t('legalPage_privacy_title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Last updated: {currentDate}</p>

            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-xl mb-2">1. Introduction and Legal Basis</h3>
                <p>This Privacy Policy explains how {CONTACT_INFO.companyName} ("we", "us", or "our") collects, uses, and protects your personal data in compliance with:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Swiss Federal Act on Data Protection (FADP)</li>
                  <li>General Data Protection Regulation (GDPR) for EU users</li>
                  {/* <li>Other applicable data protection laws</li> */}
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">2. Data Controller</h3>
                <p>The data controller responsible for your personal data is:</p>
                <p className="mt-2">
                  {CONTACT_INFO.companyName}<br />
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.zip} {CONTACT_INFO.address.city}<br />
                  {CONTACT_INFO.address.country}<br />
                  Email: <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400">{CONTACT_INFO.email.general}</a>
                </p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">3. Data We Collect</h3>
                <p><strong>3.1. Information You Provide:</strong></p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Account information (name, email, password)</li>
                  <li>Profile data (style preferences, body measurements, color preferences)</li>
                  <li>Photos you upload for virtual try-on and styling</li>
                  <li>Payment information (processed by secure third-party providers)</li>
                  <li>Booking details for consultation services</li>
                  <li>Communications with customer support</li>
                </ul>

                <p className="mt-4"><strong>3.2. Automatically Collected Data:</strong></p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Device information (browser, OS, device type)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>IP address and approximate location</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">4. How We Use Your Data</h3>
                <p>We process your personal data for the following purposes:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Service Provision:</strong> To provide AI styling, virtual try-on, and personalized recommendations</li>
                  <li><strong>Account Management:</strong> To create and maintain your user account</li>
                  <li><strong>Payment Processing:</strong> To process subscriptions and purchases</li>
                  <li><strong>Customer Support:</strong> To respond to inquiries and provide assistance</li>
                  <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our AI models</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations</li>
                  <li><strong>Marketing:</strong> To send promotional communications (with your consent)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">5. Legal Basis for Processing (GDPR)</h3>
                <p>We process your data based on:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Consent:</strong> For uploaded photos, marketing communications, and cookies</li>
                  <li><strong>Contract Performance:</strong> To provide the services you requested</li>
                  <li><strong>Legitimate Interest:</strong> For service improvement and fraud prevention</li>
                  <li><strong>Legal Obligation:</strong> For tax and accounting requirements</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">6. Data Sharing and Third Parties</h3>
                <p>We may share your data with:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Google AI Services:</strong> For AI-powered styling and image generation (Gemini API)</li>
                  <li><strong>Cloud Storage Providers:</strong> Google Cloud Storage, Oracle Cloud Infrastructure</li>
                  <li><strong>Payment Processors:</strong> For secure payment handling</li>
                  <li><strong>Analytics Services:</strong> Google Analytics (anonymized data)</li>
                  <li><strong>Email Services:</strong> For transactional and marketing emails</li>
                </ul>
                <p className="mt-2">We do NOT sell your personal data to third parties.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">7. International Data Transfers</h3>
                <p>Your data may be transferred to and processed in countries outside Switzerland/EEA, including the United States (Google services). We ensure appropriate safeguards through:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>EU-US Data Privacy Framework compliance</li>
                  <li>Standard Contractual Clauses (SCC)</li>
                  <li>Adequacy decisions by Swiss authorities</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">8. Data Retention</h3>
                <p>We retain your personal data:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Account data: Until account deletion + 30 days</li>
                  <li>Payment records: 10 years (Swiss tax law requirements)</li>
                  <li>Uploaded photos: Until deletion by you or account closure</li>
                  <li>Marketing consents: Until withdrawn</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">9. Your Rights (GDPR & Swiss FADP)</h3>
                <p>You have the right to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                  <li><strong>Restriction:</strong> Limit processing of your data</li>
                  <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                  <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
                </ul>
                <p className="mt-2">To exercise these rights, contact: <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400">{CONTACT_INFO.email.general}</a></p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">10. Data Security</h3>
                <p>We implement industry-standard security measures:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Encryption of data in transit (TLS/SSL)</li>
                  <li>Encryption of data at rest</li>
                  <li>Regular security audits and updates</li>
                  <li>Access controls and authentication</li>
                  <li>Secure cloud infrastructure</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">11. Children's Privacy</h3>
                <p>Our Service is not directed to children under 16. We do not knowingly collect data from children. If you believe a child has provided data, contact us immediately.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">12. Complaints and Supervisory Authority</h3>
                <p>If you have concerns about our data practices, you have the right to lodge a complaint with:</p>
                <p className="mt-2">
                  <strong>Swiss Federal Data Protection and Information Commissioner (FDPIC)</strong><br />
                  Feldeggweg 1<br />
                  3003 Bern, Switzerland<br />
                  Website: <a href="https://www.edoeb.admin.ch" className="text-indigo-600 dark:text-indigo-400" target="_blank" rel="noopener noreferrer">www.edoeb.admin.ch</a>
                </p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">13. Changes to Privacy Policy</h3>
                <p>We may update this Privacy Policy. Material changes will be notified via email or prominent notice on our website.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">14. Contact Us</h3>
                <p>For privacy-related questions or data protection requests:</p>
                <p className="mt-2">
                  Email: <a href={`mailto:${CONTACT_INFO.email.general}`} className="text-indigo-600 dark:text-indigo-400">{CONTACT_INFO.email.general}</a><br />
                  Address: {CONTACT_INFO.address.street}, {CONTACT_INFO.address.zip} {CONTACT_INFO.address.city}, {CONTACT_INFO.address.country}
                </p>
              </div>
            </div>
          </motion.section>

          {/* Cookie Policy */}
          <motion.section id="cookies" variants={itemVariants} className="scroll-mt-24">
            <h2 className="font-serif text-2xl font-bold mb-4">{t('legalPage_cookies_title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Last updated: {currentDate}</p>

            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-xl mb-2">1. What Are Cookies?</h3>
                <p>Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience.</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">2. Types of Cookies We Use</h3>
                <p><strong>2.1. Strictly Necessary Cookies:</strong></p>
                <p>Essential for website functionality (authentication, security). These cannot be disabled.</p>

                <p className="mt-4"><strong>2.2. Performance Cookies:</strong></p>
                <p>Google Analytics - to understand how visitors use our website (anonymized data).</p>

                <p className="mt-4"><strong>2.3. Functionality Cookies:</strong></p>
                <p>Remember your preferences (language, theme, user settings).</p>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">3. Third-Party Cookies</h3>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Google Analytics:</strong> Website analytics</li>
                  <li><strong>Payment Providers:</strong> Secure payment processing</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">4. Managing Cookies</h3>
                <p>You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.</p>
                <p className="mt-2">Learn more:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><a href="https://support.google.com/chrome/answer/95647" className="text-indigo-600 dark:text-indigo-400" target="_blank" rel="noopener noreferrer">Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-indigo-600 dark:text-indigo-400" target="_blank" rel="noopener noreferrer">Firefox</a></li>
                  <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-indigo-600 dark:text-indigo-400" target="_blank" rel="noopener noreferrer">Safari</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl mb-2">5. Your Consent</h3>
                <p>By using our website, you consent to our use of cookies as described in this policy.</p>
              </div>
            </div>
          </motion.section>
        </div>

      </div>
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default LegalPage;
